<template>
  <div class="panel-list panel-fixed my-tasks-summary">
    <panel :title="$t('Activity dashboard')" :no-button="true">
      <div v-if="loading" class="text-center py-3">
        <b-spinner small /> {{ $t('Loading') }}…
      </div>
      <template v-else>
        <div v-if="!hasAny" class="text-center text-muted py-3">
          {{ $t('There are no results to display') }}
        </div>
        <template v-else>
          <div class="summary-tiles">
            <button
              v-for="tile in tileDefs"
              :key="tile.key"
              type="button"
              class="summary-tile"
              :class="[`summary-tile--${tile.theme}`, { 'summary-tile--active': isActive(tile.key) }]"
              @click="onClick(tile.key)"
            >
              <div class="summary-tile__head">
                <span class="summary-tile__label">{{ tile.label }}</span>
                <i :class="['summary-tile__icon', tile.icon]" />
              </div>
              <div class="summary-tile__count">{{ getCount(selfCounts, tile.key) }}</div>
              <div class="summary-tile__sub">{{ tile.subtitle }}</div>
            </button>
          </div>
          <div class="completion-bar">
            <div class="completion-bar__header">
              <span class="completion-bar__title">{{ $t('Completion status') }}</span>
              <span class="completion-bar__meta">
                {{ selfCounts.completed }} {{ $t('of') }} {{ selfCounts.total }} ·
                {{ completionPercent }}%
              </span>
            </div>
            <div class="completion-bar__track" role="progressbar" :aria-valuenow="completionPercent" aria-valuemin="0" aria-valuemax="100">
              <div class="completion-bar__seg completion-bar__seg--completed" :style="{ width: pct.completed + '%' }" />
              <div class="completion-bar__seg completion-bar__seg--open" :style="{ width: pct.open + '%' }" />
              <div class="completion-bar__seg completion-bar__seg--expired" :style="{ width: pct.expired + '%' }" />
            </div>
          </div>
          <template v-if="isManagerScope">
            <div class="by-user-section">
              <div class="by-user-section__header">
                <h5 class="by-user-section__title">{{ $t('Per-user summary') }}</h5>
                <b-input
                  v-model="userSearch"
                  size="sm"
                  class="by-user-section__search"
                  :placeholder="$t('Search user…')"
                  @input="page = 1"
                />
              </div>
              <ul class="by-user-list">
                <li
                  v-for="entry in pagedUsers"
                  :key="entry.user_id"
                  class="by-user-row"
                >
                  <span class="by-user-row__avatar" :style="{ backgroundColor: avatarColor(entry) }">
                    {{ entry.initials || '?' }}
                  </span>
                  <div class="by-user-row__identity">
                    <div class="by-user-row__name">{{ entry.user_label }}</div>
                    <div v-if="entry.email_handle" class="by-user-row__handle">@{{ entry.email_handle }}</div>
                  </div>
                  <div class="by-user-row__stat by-user-row__stat--completed">
                    <strong>{{ entry.completed || 0 }}</strong>
                    <small>{{ $t('Compl.') }}</small>
                  </div>
                  <div class="by-user-row__stat by-user-row__stat--open">
                    <strong>{{ entry.open || 0 }}</strong>
                    <small>{{ $t('In progress') }}</small>
                  </div>
                  <div class="by-user-row__stat by-user-row__stat--expired">
                    <strong>{{ entry.expired || 0 }}</strong>
                    <small>{{ $t('Expired') }}</small>
                  </div>
                  <div class="by-user-row__total">
                    <strong>{{ entry.total || 0 }}</strong>
                    <small>{{ $t('Totals') }}</small>
                  </div>
                </li>
              </ul>
              <div v-if="!filteredUsers.length" class="text-muted small text-center py-2">
                {{ userSearch ? $t('No users match') : $t('No users have activities yet') }}
              </div>
              <div v-if="totalUserPages > 1" class="by-user-pagination">
                <b-pagination
                  v-model="page"
                  :total-rows="filteredUsers.length"
                  :per-page="pageSize"
                  size="sm"
                  align="center"
                  class="mb-0"
                />
              </div>
            </div>
          </template>
        </template>
      </template>
    </panel>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { axiosThux } from '@/axiosThux'
import { TYPES as MY_EXTERNAL_TASKS_TYPES } from '../my-external-tasks/store'
import { TYPES as MY_INTERNAL_TASKS_TYPES } from '../my-internal-tasks/store'

const AVATAR_PALETTE = ['#26a69a', '#5c6bc0', '#7e57c2', '#ef5350', '#ffa726', '#26c6da', '#66bb6a', '#ec407a']

export default {
  name: 'MyTasksSummary',
  props: {
    onlyMine: { type: Boolean, default: false }
  },
  data () {
    return {
      data: {},
      loading: false,
      activeFilter: 'open',
      userSearch: '',
      page: 1,
      pageSize: 5
    }
  },
  computed: {
    ...mapGetters({
      authProfile: 'auth/profile'
    }),
    profile () {
      return this.authProfile || 'generic'
    },
    isManagerScope () {
      return (this.data && this.data.scope === 'manager')
    },
    selfCounts () {
      return (this.data && this.data.self) || {}
    },
    hasAny () {
      const s = this.selfCounts
      return (s.total || 0) > 0 || (this.data.by_user || []).length > 0
    },
    completionPercent () {
      const total = this.selfCounts.total || 0
      if (!total) return 0
      return Math.round(((this.selfCounts.completed || 0) / total) * 1000) / 10
    },
    pct () {
      const total = this.selfCounts.total || 0
      if (!total) return { completed: 0, open: 0, expired: 0 }
      const completed = (this.selfCounts.completed || 0) / total * 100
      const expired = (this.selfCounts.expired || 0) / total * 100
      const openCount = Math.max(0, (this.selfCounts.open || 0) - (this.selfCounts.expired || 0))
      const open = openCount / total * 100
      return { completed, open, expired }
    },
    tileDefs () {
      const total = this.selfCounts.total || 0
      const completedPercent = total ? Math.round(((this.selfCounts.completed || 0) / total) * 1000) / 10 : 0
      const activeLabel = this.activeFilter ? this.$t('with 1 filter') : this.$t('no filter')
      return [
        {
          key: 'completed',
          label: this.$t('Completed'),
          icon: 'fas fa-check-circle',
          theme: 'completed',
          subtitle: `${completedPercent}% ${this.$t('of total')}`
        },
        {
          key: 'open',
          label: this.$t('In progress'),
          icon: 'fas fa-sync-alt',
          theme: 'open',
          subtitle: this.$t('not completed')
        },
        {
          key: 'expired',
          label: this.$t('Expired'),
          icon: 'fas fa-exclamation-triangle',
          theme: 'expired',
          subtitle: this.$t('require attention')
        },
        {
          key: 'all',
          label: this.$t('Total activities'),
          icon: 'fas fa-layer-group',
          theme: 'total',
          subtitle: activeLabel
        }
      ]
    },
    filteredUsers () {
      const all = (this.data && this.data.by_user) || []
      const q = (this.userSearch || '').trim().toLowerCase()
      if (!q) return all
      return all.filter((u) => (u.user_label || '').toLowerCase().includes(q) ||
        (u.email_handle || '').toLowerCase().includes(q))
    },
    totalUserPages () {
      return Math.ceil(this.filteredUsers.length / this.pageSize)
    },
    pagedUsers () {
      const start = (this.page - 1) * this.pageSize
      return this.filteredUsers.slice(start, start + this.pageSize)
    }
  },
  watch: {
    profile: 'fetch',
    onlyMine: 'fetch'
  },
  created () {
    // Default the dashboard lists to in-progress activities on each entry.
    // Equivalent to clicking the "In progress" tile; still toggleable from the tiles.
    // Only seed the shared summaryFilter state here (mutation, no getList): the list
    // components issue their own getList on mount with the correct `onlyMine`. Dispatching
    // the action here would fire getList before the lists set `onlyMine`, so it would read
    // the stale value left by the previous route and race the correct request, leaving wrong
    // data on screen until a reload. See setSummaryFilter action vs mutation.
    this.seedExternalFilter(this.activeFilter)
    this.seedInternalFilter(this.activeFilter)
  },
  mounted () {
    this.fetch()
  },
  methods: {
    ...mapActions({
      setExternalFilter: MY_EXTERNAL_TASKS_TYPES.GENERIC.dashboard.myexternaltasks.LIST.ACTIONS.setSummaryFilter,
      setInternalFilter: MY_INTERNAL_TASKS_TYPES.GENERIC.dashboard.myinternaltasks.LIST.ACTIONS.setSummaryFilter
    }),
    ...mapMutations({
      seedExternalFilter: MY_EXTERNAL_TASKS_TYPES.GENERIC.dashboard.myexternaltasks.LIST.MUTATIONS.setSummaryFilter,
      seedInternalFilter: MY_INTERNAL_TASKS_TYPES.GENERIC.dashboard.myinternaltasks.LIST.MUTATIONS.setSummaryFilter
    }),
    isActive (key) {
      return this.activeFilter === key || (key === 'all' && !this.activeFilter)
    },
    getCount (obj, key) {
      if (key === 'all') return obj.total || 0
      return obj[key] || 0
    },
    refresh () {
      // Public entry point used by the dashboard to reload the counters
      // (e.g. after a task is marked done in one of the sibling lists).
      this.fetch()
    },
    fetch () {
      this.loading = true
      const qs = this.onlyMine ? '?only_mine=true' : ''
      axiosThux.get(`/api/v1/task/${this.profile}/visible-task/summary/${qs}`)
        .then((resp) => { this.data = resp.data || {} })
        .catch(() => { this.data = {} })
        .finally(() => { this.loading = false })
    },
    onClick (key) {
      const newFilter = (key === 'all' || this.activeFilter === key) ? '' : key
      this.activeFilter = newFilter
      this.setExternalFilter(newFilter)
      this.setInternalFilter(newFilter)
    },
    avatarColor (entry) {
      const seed = String(entry.user_id || entry.user_label || '').split('')
        .reduce((acc, ch) => acc + ch.charCodeAt(0), 0)
      return AVATAR_PALETTE[seed % AVATAR_PALETTE.length]
    }
  }
}
</script>
